import React, { useEffect, useMemo, useState } from "react";

const EVIDENCE_TYPES = [
  "Case Study",
  "Efficacy Study",
  "Research Report",
  "ROI Analysis",
  "Third-Party Validation Report",
  "White Paper",
] as const;

export const QUALITY_INDICATORS = [
  "Safe",
  "Evidence-Based",
  "Inclusive",
  "Usable",
  "Interoperable",
] as const;

export type EvidenceType = (typeof EVIDENCE_TYPES)[number];
export type QualityIndicator = (typeof QUALITY_INDICATORS)[number];

export interface EvidenceItem {
  id: string;
  typeOfEvidence: EvidenceType | "";
  name: string; // Field 2 (max 100 chars)
  description: string; // Field 3 (max 300 chars)
  url: string; // Field 4
  publicationMonthYear: string; // Field 5: "YYYY-MM"
  qualityIndicator: QualityIndicator | ""; // Field 6
}

export interface SupportingEvidenceValue {
  items: EvidenceItem[];
}

export interface SupportingEvidenceProps {
  /** Initial JSON-backed value (used once on mount). */
  initialValue?: SupportingEvidenceValue;
  show: number;
  name?: string;
  /** Max pieces of evidence per indicator (default: 3). */
  maxPerIndicator?: number;
}

// Simple URL validation: allow empty, require http/https for non-empty strings
const isValidUrl = (value: string): boolean => {
    if (!value) return true; // treat empty as "no URL provided" and valid
    try {
        const url = new URL(value);
        return url.protocol === "http:" || url.protocol === "https:";
    } catch {
        return false;
    }
};

const createEmptyEvidence = (): EvidenceItem => ({
    id:
    typeof crypto !== "undefined" && (crypto as any).randomUUID
        ? (crypto as any).randomUUID()
        : `${Date.now()}-${Math.random()}`,
    typeOfEvidence: "",
    name: "",
    description: "",
    url: "",
    publicationMonthYear: "",
    qualityIndicator: "",
});

export const SupportingEvidence: React.FC<SupportingEvidenceProps> = ({
  initialValue,
  name,
  show,
  maxPerIndicator = 3,
}) => {
    const lastSerializedRef = React.useRef<string | null>(null);

    const [items, setItems] = useState<EvidenceItem[]>(() => {
    if (!initialValue?.items) return [];
    return initialValue.items.map((it) => ({
        ...createEmptyEvidence(),
        ...it,
        id:
        it.id ||
        (typeof crypto !== "undefined" && (crypto as any).randomUUID
            ? (crypto as any).randomUUID()
            : `${Date.now()}-${Math.random()}`),
        name: (it.name || "").slice(0, 100),
        description: (it.description || "").slice(0, 300),
    }));
    });

    // URL validation errors per evidence line (keyed by evidence id)
    const [urlErrors, setUrlErrors] = useState<Record<string, string | undefined>>(
    {}
    );

    // Filter by the QUALITY_INDICATORS[show] value, then sort
    const sortedItems = useMemo(() => {
        const selectedIndicator = QUALITY_INDICATORS[show]; // may be undefined if out of range

        const baseItems =
        selectedIndicator !== undefined
            ? items.filter((it) => it.qualityIndicator === selectedIndicator)
            : items;

        const orderMap = new Map<QualityIndicator | "", number>();
        QUALITY_INDICATORS.forEach((q, i) => orderMap.set(q, i));
        orderMap.set("", QUALITY_INDICATORS.length + 1);

        return [...baseItems].sort((a, b) => {
        const ao = orderMap.get(a.qualityIndicator) ?? 999;
        const bo = orderMap.get(b.qualityIndicator) ?? 999;
        if (ao !== bo) return ao - bo;
        return a.id.localeCompare(b.id);
        });
    }, [items, show]);


    const valueObject: SupportingEvidenceValue = useMemo(
    () => ({ items }),
    [items]
    );

    const serialized = useMemo(
    () => JSON.stringify(valueObject),
    [valueObject]
    );


    const countForIndicator = (indicator: QualityIndicator): number =>
        items.filter((it) => it.qualityIndicator === indicator).length;

        const handleAdd = (indicator: QualityIndicator) => {
        if (countForIndicator(indicator) >= maxPerIndicator) return;
        const newEvidence = { ...createEmptyEvidence(), qualityIndicator: indicator };
        setItems((prev) => [...prev, newEvidence]);
        // ensure no leftover error for new id
        setUrlErrors((prev) => {
            const next = { ...prev };
            delete next[newEvidence.id];
            return next;
        });
    };


    if (sortedItems.length === 0) {
        return (
        <div color="textSecondary">
            No supporting evidence provided.
        </div>
        );
    }

    return (
        <div >
        {name && (
            <input
            type="hidden"
            name={name}
            value={serialized}
            readOnly
            />
        )}
        <ol>
        {sortedItems.map((it) => {
            const monthYear = it.publicationMonthYear
            ? new Date(it.publicationMonthYear + "-01").toLocaleDateString(
                undefined,
                { month: "long", year: "numeric" }
                )
            : "";

            const urlIsValid = isValidUrl(it.url);

            return (
            <li
                key={it.id}
            >
                {/* Line 1: Field 2 with Field 4 as the link (only if URL is valid) */}
                <div>
                {it.url && urlIsValid ?
                    <>
                    <a
                    href={it.url}
                    target="_blank"
                    className="tooltipForOptions"
                    rel="noopener noreferrer"
                    >
                    {it.name || "Untitled Evidence"}
                    </a>
                    { it.description &&
                    <div className="popupBlack" ><div className="popupPointerBlack">&nbsp;</div> <div className="popupTextBoxBlack leftColumnPopupTextboxClass" id={"leftColumnPopupTextboxBlackEvidence"} >
                    {it.description}
                    </div></div>
                    }
                    </>
                : (
                    it.name || "Untitled Evidence"
                )}
                </div>

                {/* Line 2: Field 1–5 values in order (you trimmed description & url here) */}
                <div>
                {[
                    it.typeOfEvidence || "—", // Field 1
                    monthYear || "—", // Field 5
                ].join(" • ")}
                </div>
            </li>
            );
        })}
        </ol>
        </div>
    );

};
